/*
 * <arctic-card-stepper> — Pattern B card-level qty control.
 *
 * Default state: ATC button visible, stepper hidden.
 * After first add: stepper visible at qty in cart, ATC hidden.
 * Stepper to 0: removes line, swaps back to ATC.
 *
 * Hosts:
 *   - [data-card-stepper-add]      → ATC button
 *   - [data-card-stepper-qty]      → stepper wrapper
 *   - [data-card-stepper-input]    → qty input (number)
 *   - [data-card-stepper-decrement][data-card-stepper-increment]
 *
 * Required attributes on host element:
 *   - data-variant-id              → variant ID for /cart/add and /cart/change
 *
 * Cart sync uses Shopify's standard /cart/add.js and /cart/change.js endpoints,
 * then publishes PUB_SUB_EVENTS.cartUpdate so Northbound and cart surfaces stay
 * in sync. Card steppers do not open Dawn's cart notification; the inline qty
 * control is the confirmation UI and avoids trapping focus away from the card.
 *
 * Northbound: ATC carries [data-northbound-add-to-cart]. The theme community
 * selector intercepts that first add when no community is selected, then
 * replays it after selection. Pricing-unavailable state still disables ATC for
 * real Northbound blockers such as unpriceable variants.
 */

(() => {
if (customElements.get('arctic-card-stepper')) return;

const ATC_DEBOUNCE = 80;
const QTY_CHANGE_DEBOUNCE = 250;

class ArcticCardStepper extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.changeTimer = null;
  }

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    if (!this.variantId) return;

    this.cart = document.querySelector('cart-drawer');

    this.addButton = this.querySelector('[data-card-stepper-add]');
    this.qtyWrap = this.querySelector('[data-card-stepper-qty]');
    this.input = this.querySelector('[data-card-stepper-input]');
    this.decrement = this.querySelector('[data-card-stepper-decrement]');
    this.increment = this.querySelector('[data-card-stepper-increment]');
    this.addButtonDisabledByAvailability = this.addButton?.disabled || false;

    this.initialQty = parseInt(this.input?.value || '0', 10) || 0;
    this.applyState(this.initialQty);

    this.addButton?.addEventListener('click', this.onAddClick);
    this.decrement?.addEventListener('click', this.onDecrementClick);
    this.increment?.addEventListener('click', this.onIncrementClick);
    this.input?.addEventListener('change', this.onInputChange);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.changeTimer);
    if (this.cartUnsubscribe) this.cartUnsubscribe();
  }

  applyState(qty) {
    const showStepper = qty > 0;
    if (this.addButton) this.addButton.hidden = showStepper;
    if (this.qtyWrap) this.qtyWrap.hidden = !showStepper;
    if (this.input) this.input.value = String(qty);
    this.dataset.qty = String(qty);
  }

  setBusy(busy) {
    this.busy = busy;
    this.classList.toggle('is-busy', busy);
    if (this.addButton) this.addButton.disabled = busy || this.addButtonDisabledByAvailability;
    if (this.decrement) this.decrement.disabled = busy;
    if (this.increment) this.increment.disabled = busy;
  }

  setPending(pending) {
    this.classList.toggle('is-pending', pending);
  }

  onAddClick = (event) => {
    event.preventDefault();
    if (this.busy) return;

    if (this.addButton?.dataset.northboundPricingState === 'unavailable') {
      return;
    }

    this.setBusy(true);

    // Reveal the stepper in pending state so the loader sits where the
    // qty digit will appear once /cart/add resolves.
    if (this.addButton) this.addButton.hidden = true;
    if (this.qtyWrap) this.qtyWrap.hidden = false;
    this.setPending(true);

    // Flat single-item format ({id, quantity}) — the response is the line
    // item with `.key`, which cart-notification.renderContents needs to
    // target [id="cart-notification-product-{key}"]. The multi-item
    // ({items: [...]}) format returns a cart-shaped object with no top-level
    // key and breaks the notification render.
    const body = {
      id: parseInt(this.variantId, 10),
      quantity: 1,
    };
    const sectionsToRefresh = this.getSectionsToRefresh();

    if (sectionsToRefresh.length) {
      body.sections = sectionsToRefresh;
      body.sections_url = window.location.pathname;
    }

    if (this.cart) {
      body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          this.publishCartError(data);
          // Revert UI back to ATC since the line was not added.
          this.applyState(this.currentQty());
          return;
        }
        const newQty = (this.currentQty() || 0) + 1;
        this.applyState(newQty);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-stepper',
            productVariantId: this.variantId,
            cartData: data,
          });
        }

        this.applySectionUpdates(data, sectionsToRefresh);

        if (this.cart && typeof this.cart.renderContents === 'function') {
          if (this.cart.classList && this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
          this.cart.renderContents(data);
        }
      })
      .catch((error) => {
        console.error('[card-stepper] add failed', error);
        this.applyState(this.currentQty());
      })
      .finally(() => {
        this.setPending(false);
        this.setBusy(false);
      });
  };

  onDecrementClick = (event) => {
    event.preventDefault();
    if (this.busy) return;
    const next = Math.max(0, this.currentQty() - 1);
    this.applyState(next);
    this.queueChange(next);
  };

  onIncrementClick = (event) => {
    event.preventDefault();
    if (this.busy) return;
    const next = this.currentQty() + 1;
    this.applyState(next);
    this.queueChange(next);
  };

  onInputChange = (event) => {
    const next = Math.max(0, parseInt(event.target.value, 10) || 0);
    this.applyState(next);
    this.queueChange(next);
  };

  queueChange(qty) {
    clearTimeout(this.changeTimer);
    // Stay quiet through the debounce window so rapid +/- clicks freely
    // increment the digit without strobing a loader. The loader only fades
    // in once the fetch actually fires (changeLine).
    this.changeTimer = setTimeout(() => this.changeLine(qty), QTY_CHANGE_DEBOUNCE);
  }

  changeLine(qty) {
    this.setBusy(true);
    this.setPending(true);

    // Sections we want re-rendered for live header/cart updates without
    // re-opening the notification popup. cart-icon-bubble drives the count
    // chip; cart-notification-button drives the open-cart link if the
    // notification is currently visible.
    const sectionsToRefresh = ['cart-icon-bubble', 'cart-notification-button'];

    fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        id: this.variantId,
        quantity: qty,
        sections: sectionsToRefresh,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && typeof data.item_count === 'number') {
          this.applySectionUpdates(data, sectionsToRefresh);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'card-stepper',
              productVariantId: this.variantId,
              cartData: data,
            });
          }
        }
      })
      .catch((error) => {
        console.error('[card-stepper] change failed', error);
      })
      .finally(() => {
        this.setPending(false);
        this.setBusy(false);
      });
  }

  applySectionUpdates(data, sectionIds) {
    if (!data || !data.sections) return;
    sectionIds.forEach((id) => {
      const html = data.sections[id];
      if (!html) return;
      const target = document.getElementById(id);
      if (!target) return;
      const parsed = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.shopify-section');
      if (parsed) target.innerHTML = parsed.innerHTML;
    });
  }

  getSectionsToRefresh() {
    return ['cart-icon-bubble', 'cart-notification-button'];
  }

  refreshCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cartData) => {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-stepper',
            cartData,
          });
        }
      })
      .catch(() => {});
  }

  publishCartError(data) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'card-stepper',
        productVariantId: this.variantId,
        errors: data.description || data.message,
        message: data.message,
      });
    }
  }

  onCartUpdate = (event) => {
    if (event?.source === 'card-stepper') return;
    const cartData = event?.cartData;
    if (!cartData?.items) return;

    const variantIdInt = parseInt(this.variantId, 10);
    const line = cartData.items.find((item) => item.id === variantIdInt);
    const nextQty = line ? line.quantity : 0;

    if (nextQty !== this.currentQty()) {
      this.applyState(nextQty);
    }
  };

  currentQty() {
    return parseInt(this.dataset.qty || '0', 10) || 0;
  }
}

customElements.define('arctic-card-stepper', ArcticCardStepper);
})();
